import { useMutation, useQueryClient } from '@tanstack/react-query';
// `@/app/...`, not `@app/...`: the latter type-checks but fails `vite build`.
import { useAppDispatch } from '@/app/store/hooks';
import { invalidateDealerConfig } from '../store/dealerConfigSlice';
import {
    applyDealerConfigCatalog,
    attachDealerConfigSetting,
    deleteDealerConfigSetting,
    deleteDealerConfigSettingById,
    updateDealerConfigSetting,
    updateDealerConfigSettingById,
} from '../services/dealerConfig';
import { dealerConfigSettingKeys } from './useDealerConfigSettingsQuery';
import type {
    ApplyDealerConfigCatalogPayload,
    AttachDealerConfigSettingPayload,
    UpdateDealerConfigSettingByIdPayload,
    UpdateDealerConfigSettingPayload,
} from '../types/dealerConfig.types';

type MutatedDealer = { mutatedDealerId: string };

/**
 * Mutations invalidate both caches of this data: the React Query list key only
 * (not dealer-detail, not the catalog combobox source), and the Redux slice.
 */
export const useDealerConfigSettingMutations = (dealerId: string) => {
    const queryClient = useQueryClient();
    const dispatch = useAppDispatch();

    // A rerender swaps a pending mutation's options, so onSuccess cannot read the
    // render-time `dealerId`; onMutate context lives in state and survives.
    const captureDealer = (): MutatedDealer => ({ mutatedDealerId: dealerId });

    const invalidate = (_data: unknown, _variables: unknown, { mutatedDealerId }: MutatedDealer) => {
        dispatch(invalidateDealerConfig(mutatedDealerId));
        return queryClient.invalidateQueries({
            queryKey: dealerConfigSettingKeys.list(mutatedDealerId),
        });
    };

    const createMutation = useMutation({
        mutationFn: (payload: AttachDealerConfigSettingPayload) =>
            attachDealerConfigSetting(dealerId, [payload]),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    const updateMutation = useMutation({
        mutationFn: ({ key, ...payload }: { key: string } & UpdateDealerConfigSettingPayload) =>
            updateDealerConfigSetting(dealerId, key, payload),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    const updateByIdMutation = useMutation({
        mutationFn: ({ settingId, ...payload }: { settingId: string } & UpdateDealerConfigSettingByIdPayload) =>
            updateDealerConfigSettingById(dealerId, settingId, payload),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    const deleteMutation = useMutation({
        mutationFn: ({ key }: { key: string }) => deleteDealerConfigSetting(dealerId, key),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    const deleteByIdMutation = useMutation({
        mutationFn: ({ settingId }: { settingId: string }) => deleteDealerConfigSettingById(dealerId, settingId),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    const applyCatalogMutation = useMutation({
        mutationFn: (payload: ApplyDealerConfigCatalogPayload) => applyDealerConfigCatalog(dealerId, payload),
        onMutate: captureDealer,
        onSuccess: invalidate,
    });

    return {
        createMutation,
        updateMutation,
        updateByIdMutation,
        deleteMutation,
        deleteByIdMutation,
        applyCatalogMutation,
    };
};
